import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Pencil } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { Stars } from "@/pages/HomePage";

export default function ReviewsPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState({ reviews: [], total: 0, average: 0 });
  const [showForm, setShowForm] = useState(false);
  const [bookings, setBookings] = useState([]);

  function load() {
    api.get("/reviews", { params: { limit: 50 } }).then((r) => setData(r.data));
  }

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (user) api.get("/bookings/mine").then((r) => setBookings(r.data.filter((b) => b.status === "confirmed"))).catch(() => {});
  }, [user]);

  const eligibleBookings = bookings;

  const jsonLd = data.reviews.length ? {
    "@context": "https://schema.org",
    "@type": "LodgingBusiness",
    name: "Berdhill Rooms",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: String(data.average),
      reviewCount: String(data.total),
    },
    review: data.reviews.slice(0, 10).map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.author_name },
      reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5 },
      reviewBody: r.content,
      datePublished: r.created_at,
    })),
  } : null;

  return (
    <SiteLayout>
      <Seo
        title={`${t("review.title")} · Berdhill Rooms`}
        description={`${data.total} verified guest reviews · ${data.average} / 5 average. Read what guests say about Berdhill Rooms in Sarandë, Albania.`}
        jsonLd={jsonLd}
      />
      <div className="max-w-6xl mx-auto px-4 md:px-8 lg:px-12 py-16">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-brand-muted">{t("review.kicker")}</div>
            <h1 className="font-display text-5xl md:text-6xl mt-3">{t("review.title")}</h1>
            <div className="mt-4 flex items-center gap-3 text-sm">
              <Stars value={data.average} />
              <span className="text-brand-muted">
                {t("review.avg", { avg: data.average.toFixed(1) })} · {t("review.guest_count", { count: data.total })}
              </span>
            </div>
          </div>
          {user ? (
            eligibleBookings.length > 0 ? (
              <button data-testid="open-review-form" onClick={() => setShowForm(true)} className="bg-brand-primary text-white px-5 py-3 rounded-sm text-sm flex items-center gap-2 hover:bg-brand-primaryHover">
                <Pencil size={14} /> {t("review.write")}
              </button>
            ) : (
              <div className="text-xs text-brand-muted max-w-xs">{t("review.no_booking")}</div>
            )
          ) : (
            <Link to="/login" className="text-sm text-brand-primary hover:text-brand-primaryHover">{t("review.signin_first")}</Link>
          )}
        </div>

        {showForm && (
          <ReviewForm bookings={eligibleBookings} onClose={() => setShowForm(false)} onSubmit={() => { setShowForm(false); load(); }} />
        )}

        <div className="mt-16 grid md:grid-cols-2 gap-8">
          {data.reviews.length === 0 && (
            <div className="md:col-span-2 text-center text-brand-muted py-16" data-testid="reviews-empty">No reviews yet.</div>
          )}
          {data.reviews.map((r) => (
            <article key={r.id} data-testid={`review-${r.id}`} className={`bg-white border p-6 md:p-8 rounded-sm ${r.featured ? "border-brand-primary" : "border-brand-border"}`}>
              {r.featured && <div className="text-[10px] uppercase tracking-[0.2em] text-brand-primary mb-3 flex items-center gap-1"><Star size={11} className="fill-brand-primary" /> Featured</div>}
              <Stars value={r.rating} />
              {r.title && <h3 className="font-display text-2xl mt-3">{r.title}</h3>}
              <p className="mt-4 text-brand-text/85 leading-relaxed">"{r.content}"</p>
              <div className="mt-6 text-xs text-brand-muted flex justify-between">
                <span>{r.author_name}{r.country ? ` · ${r.country}` : ""}</span>
                {r.room_name && <span>{r.room_name}</span>}
              </div>
            </article>
          ))}
        </div>
      </div>
    </SiteLayout>
  );
}

function ReviewForm({ bookings, onClose, onSubmit }) {
  const { t } = useI18n();
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [bookingId, setBookingId] = useState(bookings[0]?.id || "");
  const [submitting, setSubmitting] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/reviews", { rating, title, content, booking_id: bookingId });
      toast.success(t("review.thanks"));
      onSubmit();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit} data-testid="review-form" className="mt-12 bg-white border border-brand-border rounded-sm p-6 md:p-8 max-w-2xl">
      <h2 className="font-display text-2xl">{t("review.write")}</h2>
      <div className="mt-6 space-y-5">
        <div>
          <label className="text-xs uppercase tracking-wider text-brand-muted">{t("review.rating")}</label>
          <div className="mt-2 flex gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" data-testid={`review-rate-${n}`} onClick={() => setRating(n)} className="p-1">
                <Star size={26} className={n <= rating ? "fill-brand-primary text-brand-primary" : "text-brand-border"} />
              </button>
            ))}
          </div>
        </div>
        {bookings.length > 1 && (
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-brand-muted">Booking</span>
            <select value={bookingId} onChange={(e) => setBookingId(e.target.value)} className="mt-1 w-full border border-brand-border rounded-sm px-3 py-2 text-sm">
              {bookings.map((b) => <option key={b.id} value={b.id}>{b.room_name} · {b.check_in}</option>)}
            </select>
          </label>
        )}
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-brand-muted">{t("review.title_field")}</span>
          <input data-testid="review-title-field" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 w-full border border-brand-border rounded-sm px-3 py-2 text-sm" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-brand-muted">{t("review.content")}</span>
          <textarea data-testid="review-content-field" rows={5} required value={content} onChange={(e) => setContent(e.target.value)} className="mt-1 w-full border border-brand-border rounded-sm px-3 py-2 text-sm" />
        </label>
      </div>
      <div className="mt-6 flex justify-end gap-3">
        <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm">Cancel</button>
        <button data-testid="review-submit" disabled={submitting} type="submit" className="bg-brand-primary text-white px-6 py-2.5 rounded-sm text-sm disabled:opacity-50">{submitting ? "…" : t("review.submit")}</button>
      </div>
    </form>
  );
}
